import { NavHeader } from '@/components/features/shared/NavHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatInvoiceType, InvoiceItem, InvoiceStatus, VoucherType } from '@/domain/invoices/invoice'
import { useDownloadDocument, useOpenDocument } from '@/hooks/api/useDocuments'
import { usePollingJob } from '@/hooks/api/useInternalJobs'
import { useDataInvoice, useGenerateInvoiceDocument } from '@/hooks/api/useInvoices'
import { Paths } from '@/routes/paths'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { formatDate } from 'date-fns'
import { ArrowLeft, CreditCard, Download, FileCheck, FileX, Loader2, Pencil, Receipt } from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageContainer } from '@/components/features/shared/PageContainer'

const statusConfig = {
  invoice: {
    pending: {
      icon: Loader2,
      color: 'text-yellow-500',
      title: 'Factura pendiente de generación',
      description: 'La factura será generada al confirmar. Este proceso envía el comprobante al servicio de AFIP.',
      buttonText: 'Generar factura',
      buttonAction: 'generate',
    },
    in_progress: {
      icon: Loader2,
      color: 'text-blue-500',
      title: 'Generando factura...',
      description: 'La factura hacia AFIP se está generando. Este proceso puede tardar unos minutos.',
      buttonText: null,
      buttonAction: null,
    },
    completed: {
      icon: FileCheck,
      color: 'text-green-500',
      title: 'Factura generada',
      description: 'La factura hacia AFIP fue generada exitosamente.',
      buttonText: 'Descargar PDF',
      buttonAction: 'download',
    },
    failed: {
      icon: FileX,
      color: 'text-red-500',
      title: 'Error en la generación',
      description: 'Hubo un problema al generar la factura AFIP.',
      buttonText: 'Reintentar',
      buttonAction: 'retry',
    },
  },
  credit_note: {
    pending: {
      icon: CreditCard,
      color: 'text-yellow-500',
      title: 'Nota de crédito pendiente de generación',
      description: 'La nota de crédito será generada al confirmar. Este proceso crea un comprobante de tipo crédito vinculado a la factura original.',
      buttonText: 'Generar nota de crédito',
      buttonAction: 'generate',
    },
    in_progress: {
      icon: Loader2,
      color: 'text-blue-500',
      title: 'Generando nota de crédito...',
      description: 'La nota de crédito se está generando. Este proceso puede tardar unos minutos.',
      buttonText: null,
      buttonAction: null,
    },
    completed: {
      icon: FileCheck,
      color: 'text-green-500',
      title: 'Nota de crédito generada',
      description: 'La nota de crédito fue generada exitosamente.',
      buttonText: 'Descargar PDF',
      buttonAction: 'download',
    },
    failed: {
      icon: FileX,
      color: 'text-red-500',
      title: 'Error en la generación',
      description: 'Hubo un problema al generar la nota de crédito.',
      buttonText: 'Reintentar',
      buttonAction: 'retry',
    },
  },
  receipt: {
    pending: {
      icon: FileCheck,
      color: 'text-yellow-500',
      title: 'Recibo pendiente de generación',
      description: 'El recibo será generado al confirmar.',
      buttonText: 'Generar recibo',
      buttonAction: 'generate',
    },
    in_progress: {
      icon: Loader2,
      color: 'text-blue-500',
      title: 'Generando recibo...',
      description: 'El recibo se está generando. Este proceso puede tardar unos minutos.',
      buttonText: null,
      buttonAction: null,
    },
    completed: {
      icon: FileCheck,
      color: 'text-green-500',
      title: 'Recibo generado',
      description: 'El recibo fue generado exitosamente.',
      buttonText: 'Descargar PDF',
      buttonAction: 'download',
    },
    failed: {
      icon: FileX,
      color: 'text-red-500',
      title: 'Error en la generación',
      description: 'Hubo un problema al generar el recibo.',
      buttonText: 'Reintentar',
      buttonAction: 'retry',
    },
  },
}

function getUrlParams() {
  const pathParts = window.location.pathname.replace('/app/management/facturacion/generacion/', '').split('?')
  const invoiceId = pathParts[0] || ''
  const searchParams = new URLSearchParams(window.location.search)
  const jobId = searchParams.get('jobId') || ''
  return { invoiceId, jobId }
}

export function InvoiceGenerationStatus() {
  const { invoiceId, jobId: initialJobId } = getUrlParams()
  const searchParams = new URLSearchParams(window.location.search)
  const voucherType: VoucherType = (searchParams.get('voucherType') as VoucherType) || VoucherType.INVOICE
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [jobId, setJobId] = useState(initialJobId)
  const { data: invoice } = useDataInvoice(invoiceId)
  const { mutate: openDocument } = useOpenDocument()
  const { mutate: downloadDocument } = useDownloadDocument()
  const generateInvoiceMutation = useGenerateInvoiceDocument()
  const { data: job, isLoading: loading, error: queryError } = usePollingJob(jobId, { interval: 1000 })
  const error = queryError?.message || null

  // console.log(voucherType)

  useEffect(() => {
    if (generateInvoiceMutation.isSuccess && generateInvoiceMutation.data?.jobId) {
      const newJobId = generateInvoiceMutation.data.jobId
      setJobId(newJobId)
      queryClient.invalidateQueries({ queryKey: ['jobs', newJobId] })
    }
  }, [generateInvoiceMutation.isSuccess, generateInvoiceMutation.data?.jobId, queryClient])

  const handleStart = () => {
    if (!invoiceId) return
    generateInvoiceMutation.mutate({ id: invoiceId, voucherType: voucherType === 'credit_note' ? VoucherType.CREDIT_NOTE : VoucherType.INVOICE })
  }

  const handleRetry = () => {
    setJobId('')
    navigate({ to: `${Paths.Management.Invoicing}` })
  }

  const invoiceDocument = job?.result?.invoices?.[0]

  const handleDownloadDocument = () => {
    if (invoiceDocument?.documentId && invoiceDocument?.fileName) {
      downloadDocument({
        documentId: invoiceDocument?.documentId!,
        fileName: invoiceDocument?.fileName!
      })
    }
  }

  const handleOpenDocument = () => {
    if (invoiceDocument?.documentId && invoiceDocument?.fileName) {
      openDocument({
        documentId: invoiceDocument?.documentId!,
        fileName: invoiceDocument?.fileName!
      })
    }
  }

  const handleCancel = () => {
    navigate({ to: `${Paths.Management.Invoicing}` })
  }

  if (error) {
    return (
      <div className="container mx-auto pb-8">
        <Card className="w-full max-w-lg mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <FileX className="w-5 h-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="flex gap-2">
              <Button onClick={handleRetry} variant="outline">Reintentar</Button>
              <Button variant="secondary" onClick={handleCancel}>Volver</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loading && !job && jobId) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardContent className="pt-12 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-muted-foreground">Consultando estado del job...</p>
        </CardContent>
      </Card>
    )
  }

  const generatedInvoice = job?.result?.invoices?.find((i: any) => i.invoiceId === invoice?.id)

  const hasError = job?.status === 'completed' && generatedInvoice?.error

  const afipErrorMessage = hasError
    ? generatedInvoice.error
    : null

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(amount)
  }

  const configKey = job?.status as keyof typeof statusConfig.invoice
  const effectiveConfig = hasError
    ? statusConfig[voucherType as 'invoice' | 'credit_note' | 'receipt'].failed
    : statusConfig[voucherType as 'invoice' | 'credit_note' | 'receipt'][configKey || 'pending']
  const config = effectiveConfig
  const Icon = config.icon

  const canEdit = invoice?.voucherType === VoucherType.RECEIPT ||
    (invoice?.voucherType === VoucherType.INVOICE && invoice?.status !== InvoiceStatus.AUTHORIZED)

  // console.log(invoice)

  const renderInvoiceDetails = () => {
    if (!invoice) return null
    return (
      <div className="mt-6 pt-6 border-t text-start">
        <h3 className="text-md font-medium mb-4">Detalle del comprobante</h3>
        <div className="grid grid-cols-2 text-sm">
          <div className="self-start py-2">
            <p className="text-muted-foreground">Nº de factura</p>
            <p className="font-medium">{invoice.invoiceNumber || 'Pendiente'}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Razón Social / Nombre legal</p>
            <p className="font-medium">{invoice.customerName || 'N/A'}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Tipo de Documento</p>
            <p className="font-medium">{invoice.customerDocType || 'N/A'}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Número de documento</p>
            <p className="font-medium">{invoice.customerDocNumber || 'N/A'}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Cliente</p>
            <p className="font-medium">{invoice.customerCode || 'Pendiente'}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Tipo comprobante actual</p>
            <p className="font-medium">{formatInvoiceType(invoice.voucherType)}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Subtotal</p>
            <p className="font-medium">{formatCurrency(invoice.subtotal)}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Impuestos</p>
            <p className="font-medium">{formatCurrency(invoice.taxAmount)}</p>
          </div>
          <div className="self-start py-2">
            <p className="text-muted-foreground">Total</p>
            <p className="font-semibold ">{formatCurrency(invoice.totalAmount)}</p>
          </div>
          {invoice.updatedAt && <div className="self-start py-2">
            <p className="text-muted-foreground">Fecha de generación</p>
            <p className="font-medium">{formatDate(invoice.updatedAt, 'dd/MM/yyyy HH:mm')}</p>
          </div>}
        </div>

        {invoice.items && invoice.items.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-3">Items de la factura</h4>
            <div className="space-y-2">
              {invoice.items.map((item: InvoiceItem, index: number) => (
                <div key={item.id} className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded">
                  <div className="w-4 text-sm font-medium text-muted-foreground">{index + 1}.</div>
                  <div className="flex-1 px-3">
                    <span className="text-sm">{item.description}</span>
                  </div>
                  <div className="flex flex-col items-center gap-3">
                    <span className="text-muted-foreground text-sm text-right">{item.quantity} x {formatCurrency(item.totalAmount)}</span>
                    <span className="font-medium text-right">{formatCurrency(item.totalAmount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <PageContainer>
      <NavHeader>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {voucherType === 'credit_note' ? 'Generación de nota de crédito' : voucherType === 'receipt' ? 'Generación de recibo' : 'Generación de factura'}
          </h1>
          <p className="text-primary text-muted-foreground">
            {voucherType === 'credit_note'
              ? 'Revise los detalles y confirme la operación para generar la nota de crédito de la factura en el servicio de facturación electrónica de AFIP'
              : voucherType === 'receipt'
              ? 'Revise los detalles y confirme la operación para generar el comprobante'
              : 'Revise los detalles y confirme la operación para generar la factura en el servicio de facturación electrónica de AFIP'}
          </p>
        </div>
      </NavHeader>
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center pb-2">
          <Icon className={`w-10 h-10 mx-auto mb-2 ${config.color}`} />
          <CardTitle>{config.title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {renderInvoiceDetails()}
          {afipErrorMessage && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-start">
              <p className="text-red-800 text-sm">{afipErrorMessage}</p>
            </div>
          )}
          <p className="text-muted-foreground mt-6 pt-6 border-t mb-4">{config.description}</p>
          {config.buttonText && (
            <div className={`flex flex-wrap items-center justify-evenly`}>
              {config.buttonAction === 'generate' && canEdit && (
                <Button variant="outline" onClick={() => navigate({ to: `${Paths.Management.EditInvoiceItems}/${invoiceId}` })}>
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar factura
                </Button>
              )}
              {config.buttonAction === 'generate' && (
                <Button variant={"info"} onClick={handleStart} disabled={generateInvoiceMutation.isPending || !!jobId}>
                  {generateInvoiceMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Receipt className="w-4 h-4 mr-2" />
                  )}
                  {config.buttonText}
                </Button>
              )}
              {config.buttonAction === 'start' && (
                <Button onClick={handleStart}>
                  <Loader2 className="w-4 h-4 mr-2" />
                  {config.buttonText}
                </Button>
              )}
              {config.buttonAction === 'download' && (
                <Button variant={"info"} onClick={handleDownloadDocument}>
                  <Download className="w-4 h-4 mr-2" />
                  {config.buttonText}
                </Button>
              )}
              {config.buttonAction === 'download' && (
                <Button variant={"info"} onClick={handleOpenDocument}>
                  <FileCheck className="w-4 h-4 mr-2" />
                  {"Abrir PDF"}
                </Button>
              )}
              {config.buttonAction === 'retry' && (
                <Button onClick={handleRetry} variant="outline">
                  <Loader2 className="w-4 h-4 mr-2" />
                  {config.buttonText}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <div className="mt-6 text-left">
        <Button variant="outline" onClick={handleCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    </PageContainer>
  )
}